import API_BASE from '../config';
import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import CompanyDetailView from '../components/CompanyDetailView';
import ConfirmActionModal from '../components/ConfirmActionModal';
import { creditAwareFetch } from '../hooks/creditAwareFetch';

const SIGNAL_LABELS = {
  funding: 'Funding',
  leadership_change: 'Leadership',
  hiring: 'Hiring',
  tech_change: 'Tech',
  news: 'News'
};

const SIGNAL_COLOURS = {
  funding: '#276749',
  leadership_change: '#2B6CB0',
  hiring: '#B7791F',
  tech_change: '#6B46C1',
  news: '#888'
};

const SIGNAL_BG = {
  funding: '#F0FFF4',
  leadership_change: '#EBF8FF',
  hiring: '#FFFAF0',
  tech_change: '#FAF5FF',
  news: '#F7F7F7'
};

const FILTER_TYPES = [
  { key: 'all', label: 'All' },
  { key: 'funding', label: 'Funding' },
  { key: 'leadership_change', label: 'Leadership' },
  { key: 'hiring', label: 'Hiring' },
  { key: 'tech_change', label: 'Tech' },
  { key: 'news', label: 'News' }
];

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function relativeTime(d) {
  if (!d) return '';
  const diff = Date.now() - new Date(d).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  if (days > 30) return formatDate(d);
  if (days > 0) return days + 'd';
  if (hours > 0) return hours + 'h';
  if (mins > 0) return mins + 'm';
  return 'now';
}

export default function Intelligence() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const urlCompanyId = new URLSearchParams(location.search).get("companyId");

  const [client, setClient] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [companySearch, setCompanySearch] = useState('');
  const [companyFilter, setCompanyFilter] = useState('all'); // all | fresh | due | never
  const [selectedCompanyId, setSelectedCompanyId] = useState(urlCompanyId ? parseInt(urlCompanyId) : null);
  const [bulkSelected, setBulkSelected] = useState(new Set());

  const [feed, setFeed] = useState([]);
  const [stats, setStats] = useState({ total: 0, fresh: 0, due: 0, never_swept: 0 });
  const [feedFilter, setFeedFilter] = useState('all');
  const [feedLoading, setFeedLoading] = useState(true);
  const [feedLimit, setFeedLimit] = useState(50);
  const [feedHasMore, setFeedHasMore] = useState(true);
  const [centreInitialTab, setCentreInitialTab] = useState(undefined);

  const [showAdvancedSweep, setShowAdvancedSweep] = useState(false);
  const [confirmSweepOpen, setConfirmSweepOpen] = useState(false);
  const [pendingSweep, setPendingSweep] = useState({ ids: [], prompt: '' });
  const [advancedPrompt, setAdvancedPrompt] = useState('');
  const [bulkSweeping, setBulkSweeping] = useState(false);
  const [scanStopped, setScanStopped] = useState({ global: false, client: false });
  const [scanToggling, setScanToggling] = useState(false);

  // Mobile responsive
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 900 : false);
  const [mobileTab, setMobileTab] = useState('detail'); // companies | detail | feed
  useEffect(() => {
    function handleResize() { setIsMobile(window.innerWidth < 900); }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch client + companies on mount
  useEffect(() => {
    if (!id) return;
    fetch(API_BASE + '/clients/' + id).then(r => r.json()).then(setClient).catch(() => {});
    loadCompanies();
    loadScanStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function loadScanStatus() {
    try {
      const res = await fetch(API_BASE + '/clients/' + id + '/scan-status');
      if (!res.ok) return;
      const data = await res.json();
      setScanStopped({ global: !!data.global_stopped, client: !!data.client_stopped });
    } catch (_) {}
  }

  async function toggleScan(scope) {
    setScanToggling(true);
    try {
      const stopped = scope === 'global' ? scanStopped.global : scanStopped.client;
      const endpoint = stopped ? '/scan/resume' : '/scan/stop';
      const body = scope === 'client' ? { client_id: parseInt(id) } : {};
      await fetch(API_BASE + endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      await loadScanStatus();
    } catch (_) {} finally { setScanToggling(false); }
  }

  // Fetch feed when filter or limit changes, or after sweep
  useEffect(() => {
    if (!id) return;
    loadFeed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, feedFilter, feedLimit]);

  // Reset feed limit when filter changes
  useEffect(() => {
    setFeedLimit(50);
  }, [feedFilter]);

  // Auto-select a company on first load if none selected
  useEffect(() => {
    if (!selectedCompanyId && companies.length > 0) {
      setSelectedCompanyId(companies[0].id);
    }
  }, [companies, selectedCompanyId]);

  async function loadCompanies() {
    try {
      const res = await fetch(API_BASE + '/clients/' + id + '/sweep-status');
      if (!res.ok) {
        // Fallback to plain companies endpoint if sweep-status not available
        const fallback = await fetch(API_BASE + '/clients/' + id + '/companies').then(r => r.json());
        setCompanies(fallback.map(c => ({ ...c, sweep_status: 'never' })));
        return;
      }
      const data = await res.json();
      setCompanies(data.companies || []);
    } catch (e) {
      console.error('Failed to load companies', e);
    }
  }

  async function loadFeed() {
    setFeedLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(feedLimit) });
      if (feedFilter !== 'all') params.set('signal_type', feedFilter);
      const res = await fetch(API_BASE + '/clients/' + id + '/intelligence/feed?' + params.toString());
      if (!res.ok) throw new Error('Failed to load feed');
      const data = await res.json();
      setFeed(data.feed || []);
      setStats(data.stats || { total: 0, fresh: 0, due: 0, never_swept: 0 });
      setFeedHasMore((data.feed || []).length >= feedLimit);
    } catch (e) {
      console.error('Feed load failed', e);
    }
    setFeedLoading(false);
  }

  function selectCompany(companyId) {
    setSelectedCompanyId(companyId);
    // Update URL without full reload
    navigate('/clients/' + id + '/intelligence?companyId=' + companyId, { replace: true });
  }

  function toggleBulkSelect(companyId) {
    setBulkSelected(prev => {
      const next = new Set(prev);
      if (next.has(companyId)) next.delete(companyId);
      else next.add(companyId);
      return next;
    });
  }

  async function runBulkSweep(companyIds, prompt = '') {
    if (!companyIds.length) return;
    setPendingSweep({ ids: companyIds, prompt });
    setConfirmSweepOpen(true);
  }

  async function doBulkSweep(companyIds, prompt = '') {
    setConfirmSweepOpen(false);
    if (!companyIds.length) return;
    setBulkSweeping(true);
    const toastId = toast.loading('Researching ' + companyIds.length + ' companies — this may take a minute...');
    try {
      const res = await creditAwareFetch(API_BASE + '/clients/' + id + '/intelligence/sweep', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ company_ids: companyIds, prompt })
      });
      if (!res) return;
      if (!res.ok) {
        if (res.status === 499) {
          toast('Research stopped', { id: toastId, icon: '⏹️' });
          setBulkSweeping(false);
          return;
        }
        throw new Error(await res.text());
      }
      toast.success('Researched ' + companyIds.length + ' companies', { id: toastId });
      setBulkSelected(new Set());
      setShowAdvancedSweep(false);
      setAdvancedPrompt('');
      await loadCompanies();
      await loadFeed();
    } catch (e) {
      toast.error('Sweep failed: ' + e.message, { id: toastId });
    }
    setBulkSweeping(false);
  }

  async function stopDigest() {
    try {
      await fetch(API_BASE + '/jobs/stop', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind: 'digest', client_id: parseInt(id) })
      });
      toast('Stopping research...', { icon: '⏹️' });
    } catch (_) {}
  }

  function handleSweepAllDue() {
    const dueIds = companies.filter(c => c.sweep_status !== 'fresh').map(c => c.id);
    if (!dueIds.length) {
      toast('All companies are up to date.', { icon: 'ℹ️' });
      return;
    }
    runBulkSweep(dueIds);
  }

  function handleAdvancedSweep() {
    const ids = bulkSelected.size > 0 ? Array.from(bulkSelected) : companies.map(c => c.id);
    runBulkSweep(ids, advancedPrompt);
  }

  // Filter companies for left rail
  const filteredCompanies = companies.filter(c => {
    if (companySearch && !c.name?.toLowerCase().includes(companySearch.toLowerCase())) return false;
    if (companyFilter === 'fresh' && c.sweep_status !== 'fresh') return false;
    if (companyFilter === 'due' && c.sweep_status !== 'due') return false;
    if (companyFilter === 'never' && c.sweep_status !== 'never') return false;
    return true;
  });

  return (
    <div style={{ backgroundColor: '#FAFAF8', minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
      {/* Top bar */}
      <div style={{ backgroundColor: '#fff', borderBottom: '0.5px solid #E8E4E0', padding: isMobile ? '0.75rem 1rem' : '1rem 1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', flexWrap: 'wrap', gap: isMobile ? '0.6rem' : '1rem', flexDirection: isMobile ? 'column' : 'row' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button onClick={() => navigate('/clients/' + id)} style={{ background: 'none', border: 'none', color: '#888', fontSize: '0.85rem', cursor: 'pointer', padding: 0 }}>
            ← {client?.name || 'Back'}
          </button>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.3rem', fontWeight: '600', color: '#1A1A1A' }}>Intelligence</h1>
            {client && <p style={{ margin: '0.1rem 0 0', fontSize: '0.78rem', color: '#888' }}>{client.name}</p>}
          </div>
        </div>

        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
          {!isMobile && (
            <div style={{ display: 'flex', gap: '0.5rem', fontSize: '0.78rem' }}>
              <StatPill label="All" value={stats.total} colour="#1A1A1A" active={companyFilter === 'all'} onClick={() => setCompanyFilter('all')} />
              <StatPill label="Fresh" value={stats.fresh} colour="#276749" active={companyFilter === 'fresh'} onClick={() => setCompanyFilter('fresh')} />
              <StatPill label="Due" value={stats.due} colour="#B7791F" active={companyFilter === 'due'} onClick={() => setCompanyFilter('due')} />
              <StatPill label="Never" value={stats.never_swept} colour="#888" active={companyFilter === 'never'} onClick={() => setCompanyFilter('never')} />
            </div>
          )}
          {(() => {
            const dueCount = companies.filter(c => c.sweep_status !== 'fresh').length;
            return (
              <>
                <button onClick={handleSweepAllDue} disabled={bulkSweeping || dueCount === 0}
                  style={{ fontSize: '0.8rem', color: '#fff', background: '#8B1A1A', border: 'none', borderRadius: '6px', padding: '0.5rem 0.95rem', cursor: bulkSweeping ? 'not-allowed' : 'pointer', opacity: bulkSweeping || dueCount === 0 ? 0.5 : 1, fontWeight: '500' }}>
                  {bulkSweeping ? 'Researching...' : dueCount === 0 ? 'All up to date' : 'Sweep ' + dueCount + ' due'}
                </button>
                {bulkSweeping && (
                  <button onClick={stopDigest}
                    style={{ fontSize: '0.8rem', color: '#fff', background: '#B7791F', border: 'none', borderRadius: '6px', padding: '0.5rem 0.95rem', cursor: 'pointer', fontWeight: '500', marginLeft: '0.5rem' }}>
                    Stop research
                  </button>
                )}
              </>
            );
          })()}
<button onClick={() => setShowAdvancedSweep(true)}
            style={{ fontSize: '0.8rem', color: '#1A1A1A', background: 'none', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.5rem 0.85rem', cursor: 'pointer' }}>
            Advanced research
          </button>
        </div>
      </div>

      {/* Mobile tab switcher */}
      {isMobile && (
        <div style={{ display: 'flex', backgroundColor: '#fff', borderBottom: '0.5px solid #E8E4E0', flexShrink: 0 }}>
          {[
            { key: 'companies', label: 'Companies', count: companies.length },
            { key: 'detail', label: 'Selected', count: null },
            { key: 'feed', label: 'Activity', count: feed.length }
          ].map(t => (
            <button key={t.key} onClick={() => setMobileTab(t.key)}
              style={{ flex: 1, padding: '0.75rem 0.5rem', background: 'none', border: 'none', borderBottom: mobileTab === t.key ? '2px solid #8B1A1A' : '2px solid transparent', color: mobileTab === t.key ? '#8B1A1A' : '#888', fontSize: '0.85rem', cursor: 'pointer', fontWeight: mobileTab === t.key ? '600' : '400', marginBottom: '-1px' }}>
              {t.label}{t.count !== null ? ' (' + t.count + ')' : ''}
            </button>
          ))}
        </div>
      )}

      {/* Body — three columns on desktop, single tab-switched column on mobile */}
      <div style={{ display: isMobile ? 'block' : 'grid', gridTemplateColumns: isMobile ? undefined : '22% 56% 22%', height: isMobile ? 'auto' : 'calc(100vh - 130px)', minHeight: isMobile ? 'calc(100vh - 130px)' : undefined, overflow: 'hidden' }}>

        {/* LEFT RAIL — Companies list */}
        <div style={{ display: isMobile && mobileTab !== 'companies' ? 'none' : 'flex', borderRight: isMobile ? 'none' : '0.5px solid #E8E4E0', backgroundColor: '#fff', flexDirection: 'column', overflow: 'hidden', height: isMobile ? 'calc(100vh - 180px)' : 'auto' }}>
          <div style={{ padding: '0.75rem', borderBottom: '0.5px solid #E8E4E0', flexShrink: 0 }}>
            <input value={companySearch} onChange={e => setCompanySearch(e.target.value)} placeholder="Search companies..."
              style={{ width: '100%', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.4rem 0.6rem', fontSize: '0.8rem', boxSizing: 'border-box', outline: 'none' }} />
            {isMobile && (
              <div style={{ display: 'flex', gap: '0.3rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                {[
                  { key: 'all', label: 'All', count: stats.total },
                  { key: 'fresh', label: 'Fresh', count: stats.fresh },
                  { key: 'due', label: 'Due', count: stats.due },
                  { key: 'never', label: 'Never', count: stats.never_swept }
                ].map(f => (
                  <button key={f.key} onClick={() => setCompanyFilter(f.key)}
                    style={{ fontSize: '0.7rem', padding: '0.2rem 0.5rem', border: '1px solid ' + (companyFilter === f.key ? '#8B1A1A' : '#D4CFC9'), borderRadius: '12px', background: companyFilter === f.key ? '#8B1A1A' : 'none', color: companyFilter === f.key ? '#fff' : '#666', cursor: 'pointer', fontWeight: '500' }}>
                    {f.label} {f.count > 0 && <span style={{ opacity: 0.8 }}>({f.count})</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div style={{ flex: 1, overflowY: 'auto' }}>
            {filteredCompanies.length === 0 ? (
              <p style={{ padding: '1.5rem 0.85rem', fontSize: '0.8rem', color: '#888', textAlign: 'center' }}>No companies match.</p>
            ) : (
              filteredCompanies.map(c => {
                const isSelected = selectedCompanyId === c.id;
                const isBulkSelected = bulkSelected.has(c.id);
                const statusColour = c.sweep_status === 'fresh' ? '#276749' : c.sweep_status === 'due' ? '#B7791F' : '#D4CFC9';
                return (
                  <div key={c.id}
                    style={{ padding: '0.65rem 0.85rem', borderBottom: '0.5px solid #F0EDE9', cursor: 'pointer', backgroundColor: isSelected ? '#FFF5F5' : 'transparent', borderLeft: isSelected ? '3px solid #8B1A1A' : '3px solid transparent', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}
                    onClick={() => selectCompany(c.id)}>
                    <input type="checkbox" checked={isBulkSelected} onChange={e => { e.stopPropagation(); toggleBulkSelect(c.id); }} onClick={e => e.stopPropagation()}
                      style={{ marginTop: '0.2rem', accentColor: '#8B1A1A', cursor: 'pointer', flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.3rem' }}>
                        <p style={{ margin: 0, fontSize: '0.82rem', fontWeight: isSelected ? '600' : '500', color: '#1A1A1A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</p>
                        <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: statusColour, flexShrink: 0 }}></span>
                      </div>
                      <p style={{ margin: '0.1rem 0 0', fontSize: '0.7rem', color: '#888' }}>
                        {c.last_swept_at ? relativeTime(c.last_swept_at) : 'never'}
                        {c.last_sweep_signal_count > 0 && <span style={{ marginLeft: '0.4rem', color: '#276749', fontWeight: '500' }}>· {c.last_sweep_signal_count} signal{c.last_sweep_signal_count === 1 ? '' : 's'}</span>}
                        {Number(c.digest_count) > 0 && <span style={{ marginLeft: '0.4rem', color: '#8B1A1A', fontWeight: '500' }}>· {c.digest_count} digest{Number(c.digest_count) === 1 ? '' : 's'}</span>}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {bulkSelected.size > 0 && (
            <div style={{ padding: '0.75rem', borderTop: '1px solid #8B1A1A', backgroundColor: '#FFF5F5', display: 'flex', flexDirection: 'column', gap: '0.5rem', flexShrink: 0, position: 'sticky', bottom: 0, zIndex: 10, boxShadow: '0 -4px 12px rgba(0,0,0,0.06)' }}>
              <p style={{ margin: 0, fontSize: '0.75rem', color: '#8B1A1A', fontWeight: '500' }}>{bulkSelected.size} selected</p>
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                <button onClick={() => runBulkSweep(Array.from(bulkSelected))} disabled={bulkSweeping}
                  style={{ flex: 1, fontSize: '0.82rem', color: '#fff', background: '#8B1A1A', border: 'none', borderRadius: '6px', padding: '0.55rem 0.6rem', cursor: bulkSweeping ? 'not-allowed' : 'pointer', opacity: bulkSweeping ? 0.6 : 1, fontWeight: '600' }}>
                  {bulkSweeping ? 'Sweeping...' : 'Sweep ' + bulkSelected.size + ' compan' + (bulkSelected.size === 1 ? 'y' : 'ies')}
                </button>
                <button onClick={() => setBulkSelected(new Set())} disabled={bulkSweeping}
                  style={{ fontSize: '0.82rem', color: '#666', background: '#fff', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.55rem 0.7rem', cursor: 'pointer', fontWeight: '500' }}>
                  Clear
                </button>
              </div>
            </div>
          )}
        </div>

        {/* CENTRE — CompanyDetailView */}
        <div style={{ display: isMobile && mobileTab !== 'detail' ? 'none' : 'block', overflow: 'hidden', height: isMobile ? 'calc(100vh - 130px)' : 'auto' }}>
          <CompanyDetailView
            companyId={selectedCompanyId}
            clientId={id}
            initialTab={centreInitialTab}
            onSweepComplete={() => { loadCompanies(); loadFeed(); }}
          />
        </div>

        {/* RIGHT RAIL — Cross-company feed */}
        <div style={{ display: isMobile && mobileTab !== 'feed' ? 'none' : 'flex', borderLeft: isMobile ? 'none' : '0.5px solid #E8E4E0', backgroundColor: '#fff', flexDirection: 'column', overflow: 'hidden', height: isMobile ? 'calc(100vh - 180px)' : 'auto' }}>
          <div style={{ padding: '0.75rem', borderBottom: '0.5px solid #E8E4E0', flexShrink: 0 }}>
            <p style={{ margin: '0 0 0.5rem', fontSize: '0.72rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: '600' }}>Activity feed</p>
            <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
              {FILTER_TYPES.map(f => (
                <button key={f.key} onClick={() => setFeedFilter(f.key)}
                  style={{ fontSize: '0.7rem', padding: '0.2rem 0.5rem', border: '1px solid ' + (feedFilter === f.key ? '#8B1A1A' : '#D4CFC9'), borderRadius: '12px', background: feedFilter === f.key ? '#8B1A1A' : 'none', color: feedFilter === f.key ? '#fff' : '#666', cursor: 'pointer', fontWeight: '500' }}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '0.75rem' }}>
            {feedLoading ? (
              <p style={{ padding: '2rem', textAlign: 'center', color: '#888', fontSize: '0.85rem' }}>Loading...</p>
            ) : feed.length === 0 ? (
              <EmptyFeedState filter={feedFilter} />
            ) : (
              <>
                {groupFeedByDate(feed).map(group => (
                  <div key={group.label} style={{ marginBottom: '0.85rem' }}>
                    <p style={{ margin: '0 0 0.4rem', fontSize: '0.66rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: '600' }}>{group.label}</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {group.items.map(item => (
                        <FeedCard key={item.feed_type + '-' + item.id} item={item}
                          onClick={() => {
                            if (item.feed_type === 'signal' && item.company_id) {
                              setCentreInitialTab('signals');
                              selectCompany(item.company_id);
                            } else if (item.feed_type === 'digest' && item.company_ids?.length === 1) {
                              setCentreInitialTab('digest');
                              selectCompany(item.company_ids[0]);
                            }
                          }} />
                      ))}
                    </div>
                  </div>
                ))}
                {feedHasMore && (
                  <button onClick={() => setFeedLimit(l => l + 50)} disabled={feedLoading}
                    style={{ width: '100%', marginTop: '0.5rem', fontSize: '0.78rem', color: '#8B1A1A', background: '#fff', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.5rem', cursor: feedLoading ? 'not-allowed' : 'pointer', fontWeight: '500', opacity: feedLoading ? 0.6 : 1 }}>
                    {feedLoading ? 'Loading...' : 'Load more'}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Advanced sweep modal */}
      {showAdvancedSweep && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowAdvancedSweep(false)}>
          <div style={{ width: '500px', maxWidth: '90vw', backgroundColor: '#fff', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 12px 48px rgba(0,0,0,0.18)' }} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 0.4rem', fontSize: '1.1rem', fontWeight: '600', color: '#1A1A1A' }}>Advanced research</h2>
            <p style={{ margin: '0 0 1rem', fontSize: '0.85rem', color: '#666', lineHeight: '1.5' }}>
              Focus the AI on a specific topic across {bulkSelected.size > 0 ? bulkSelected.size + ' selected ' + (bulkSelected.size === 1 ? 'company' : 'companies') : 'all ' + companies.length + ' companies'}.
              Useful when you need answers about a theme — funding rounds, leadership changes, specific products, regulatory impact.
              Leave blank to run a generic sweep.
            </p>
            <textarea value={advancedPrompt} onChange={e => setAdvancedPrompt(e.target.value)}
              placeholder="e.g. 'Focus on UK accounting firms hiring senior auditors after the EU CSRD ruling' or 'Find companies impacted by the new R&D tax credit changes'"
              style={{ width: '100%', minHeight: '100px', padding: '0.7rem', fontSize: '0.85rem', border: '1px solid #D4CFC9', borderRadius: '6px', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button onClick={() => setShowAdvancedSweep(false)}
                style={{ fontSize: '0.85rem', color: '#888', background: 'none', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.5rem 0.95rem', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={handleAdvancedSweep} disabled={bulkSweeping}
                style={{ fontSize: '0.85rem', color: '#fff', background: '#8B1A1A', border: 'none', borderRadius: '6px', padding: '0.5rem 0.95rem', cursor: 'pointer', opacity: bulkSweeping ? 0.6 : 1, fontWeight: '500' }}>
                {bulkSweeping ? 'Researching...' : 'Run research'}
              </button>
              {bulkSweeping && (
                <button onClick={stopDigest}
                  style={{ fontSize: '0.85rem', color: '#fff', background: '#B7791F', border: 'none', borderRadius: '6px', padding: '0.5rem 0.95rem', cursor: 'pointer', fontWeight: '500' }}>
                  Stop
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmActionModal
        open={confirmSweepOpen}
        title={'Research ' + pendingSweep.ids.length + ' compan' + (pendingSweep.ids.length === 1 ? 'y' : 'ies') + '?'}
        body={'This will run intelligence research on ' + pendingSweep.ids.length + ' compan' + (pendingSweep.ids.length === 1 ? 'y' : 'ies') + '. This may take a minute and consumes credits.'}
        confirmLabel={'Research ' + pendingSweep.ids.length}
        onConfirm={() => doBulkSweep(pendingSweep.ids, pendingSweep.prompt)}
        onCancel={() => setConfirmSweepOpen(false)}
      />
    </div>
  );
}

function StatPill({ label, value, colour, active, onClick }) {
  const clickable = !!onClick;
  return (
    <button
      onClick={onClick}
      disabled={!clickable}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
        padding: '0.3rem 0.65rem',
        backgroundColor: active ? colour : colour + '15',
        color: active ? '#fff' : colour,
        border: active ? '1px solid ' + colour : '1px solid transparent',
        borderRadius: '14px', fontSize: '0.74rem', fontWeight: '600',
        cursor: clickable ? 'pointer' : 'default',
        outline: 'none',
        transition: 'all 0.12s'
      }}>
      <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: active ? '#fff' : colour }}></span>
      {value} {label}
    </button>
  );
}

function groupFeedByDate(items) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const yesterday = today - 24 * 60 * 60 * 1000;
  const thisWeekStart = today - now.getDay() * 24 * 60 * 60 * 1000;
  const lastWeekStart = thisWeekStart - 7 * 24 * 60 * 60 * 1000;

  const groups = {
    today: { label: 'Today', items: [] },
    yesterday: { label: 'Yesterday', items: [] },
    thisWeek: { label: 'This week', items: [] },
    lastWeek: { label: 'Last week', items: [] },
    earlier: { label: 'Earlier', items: [] }
  };

  items.forEach(item => {
    const dateStr = item.feed_date || item.detected_at || item.created_at;
    if (!dateStr) { groups.earlier.items.push(item); return; }
    const t = new Date(dateStr).getTime();
    if (t >= today) groups.today.items.push(item);
    else if (t >= yesterday) groups.yesterday.items.push(item);
    else if (t >= thisWeekStart) groups.thisWeek.items.push(item);
    else if (t >= lastWeekStart) groups.lastWeek.items.push(item);
    else groups.earlier.items.push(item);
  });

  return Object.values(groups).filter(g => g.items.length > 0);
}

function FeedCard({ item, onClick }) {
  if (item.feed_type === 'digest') {
    const companies = item.company_names || [];
    const headline = companies.length === 1 ? companies[0] : companies.length + ' companies';
    const summary = (item.edited_digest || item.generated_digest || '').substring(0, 200);
    return (
      <div onClick={() => companies.length === 1 && onClick && onClick()} style={{ backgroundColor: '#FAFAF8', border: '0.5px solid #E8E4E0', borderRadius: '8px', padding: '0.7rem 0.85rem', cursor: companies.length === 1 ? 'pointer' : 'default', borderLeft: '3px solid #8B1A1A' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.3rem' }}>
          <span style={{ fontSize: '0.66rem', fontWeight: '700', color: '#8B1A1A', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Digest</span>
          <span style={{ fontSize: '0.68rem', color: '#888' }}>{relativeTime(item.created_at)}</span>
        </div>
        <p style={{ margin: '0 0 0.25rem', fontSize: '0.78rem', fontWeight: '600', color: '#1A1A1A' }}>{headline}</p>
        <p style={{ margin: 0, fontSize: '0.74rem', color: '#666', lineHeight: '1.4', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{summary}</p>
        {item.sent_status === 'sent' && <span style={{ fontSize: '0.66rem', color: '#276749', fontWeight: '600', marginTop: '0.3rem', display: 'inline-block' }}>✓ Sent</span>}
      </div>
    );
  }

  const colour = SIGNAL_COLOURS[item.signal_type] || '#888';
  const bg = SIGNAL_BG[item.signal_type] || '#F7F7F7';
  return (
    <div onClick={onClick} style={{ backgroundColor: bg, borderRadius: '8px', padding: '0.7rem 0.85rem', cursor: 'pointer', borderLeft: '3px solid ' + colour }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.3rem' }}>
        <span style={{ fontSize: '0.66rem', fontWeight: '700', color: colour, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{SIGNAL_LABELS[item.signal_type] || item.signal_type}</span>
        <span style={{ fontSize: '0.68rem', color: '#888' }}>{relativeTime(item.detected_at || item.created_at)}</span>
      </div>
      <p style={{ margin: '0 0 0.25rem', fontSize: '0.78rem', fontWeight: '600', color: '#1A1A1A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.company_name}</p>
      <p style={{ margin: '0 0 0.3rem', fontSize: '0.74rem', color: '#1A1A1A', lineHeight: '1.4', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{item.signal_data?.summary}</p>
      {item.source && (
        <p style={{ margin: 0, fontSize: '0.68rem', color: '#888' }}>{item.source}{item.source_url ? ' ↗' : ''}</p>
      )}
    </div>
  );
}

function EmptyFeedState({ filter }) {
  // If filtered to a specific signal type, show a contextual message
  if (filter && filter !== 'all') {
    const label = SIGNAL_LABELS[filter] || filter;
    return (
      <div style={{ padding: '2rem 1rem', textAlign: 'center' }}>
        <p style={{ margin: '0 0 0.4rem', fontSize: '0.85rem', fontWeight: '600', color: '#666' }}>
          No {label.toLowerCase()} signals yet
        </p>
        <p style={{ margin: 0, fontSize: '0.75rem', color: '#888', lineHeight: '1.5' }}>
          {label} signals will appear here when detected by the daily sweep or after running research.
        </p>
      </div>
    );
  }

  // Default: show "what to expect" sample cards
  return (
    <div style={{ padding: '0.5rem' }}>
      <div style={{ padding: '1rem 0.5rem 0.85rem' }}>
        <p style={{ margin: '0 0 0.3rem', fontSize: '0.9rem', fontWeight: '600', color: '#1A1A1A', textAlign: 'center' }}>
          No activity yet
        </p>
        <p style={{ margin: 0, fontSize: '0.75rem', color: '#888', lineHeight: '1.5', textAlign: 'center' }}>
          Run a sweep to populate this feed.<br />Here's what you'll see:
        </p>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', opacity: 0.55 }}>
        {[
          { type: 'funding', name: 'Acme Corp', summary: 'Raised £5m Series B led by SVB Capital', source: 'Crunchbase' },
          { type: 'leadership_change', name: 'GlobalCo', summary: 'New CRO appointed from Salesforce', source: 'LinkedIn' },
          { type: 'hiring', name: 'TechCo', summary: 'Now hiring 12 engineers and 4 sales reps', source: 'Company website' }
        ].map((eg, i) => {
          const colour = SIGNAL_COLOURS[eg.type];
          const bg = SIGNAL_BG[eg.type];
          return (
            <div key={i} style={{ backgroundColor: bg, borderRadius: '8px', padding: '0.7rem 0.85rem', borderLeft: '3px solid ' + colour }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                <span style={{ fontSize: '0.66rem', fontWeight: '700', color: colour, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{SIGNAL_LABELS[eg.type]}</span>
                <span style={{ fontSize: '0.68rem', color: '#888' }}>example</span>
              </div>
              <p style={{ margin: '0 0 0.25rem', fontSize: '0.78rem', fontWeight: '600', color: '#1A1A1A' }}>{eg.name}</p>
              <p style={{ margin: '0 0 0.3rem', fontSize: '0.74rem', color: '#1A1A1A', lineHeight: '1.4' }}>{eg.summary}</p>
              <p style={{ margin: 0, fontSize: '0.68rem', color: '#888' }}>{eg.source}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
